import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';

import { ApiUrlService } from 'src/app/services/api-url.service';
import { ApiService } from 'src/app/services/api.service';
import { DataService } from 'src/app/services/data.service';
import { LoadingService } from 'src/app/services/loading.service';

import { BatchHeaderColDefs } from 'src/app/models/batch-header-col-defs.model';
import { BatchIdCellRendererComponent } from './batch-id-cell-renderer.component';

export interface BreadcrumbItem {
  label: string;
  link: string | null;
}

@Component({
  standalone: true,
    selector: 'app-ema-batch-headers',
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatButtonModule,
        AgGridAngular
    ],
    templateUrl: './ema-batch-headers.component.html',
    styleUrls: ['./ema-batch-headers.component.scss']
})


export class EmaBatchHeadersComponent implements OnInit, OnDestroy {

  breadcrumbs: BreadcrumbItem[] = [];
  rowData: any[] = [];
  filteredRowData: any[] = [];
  textFilter = '';
  totalRowCount = 0;
  currentPage = 1;
  gridApi!: GridApi;
  gridContext: any;
  gridOptions: any;

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true
  };
  columnDefs: ColDef[] = [];

  filters = {
    startDate: null as Date | null,
    endDate: null as Date | null,
    status: '',
    importStep: '',
    transmitStep: '',
    transformationStep: ''
  };

  constructor(
    public loadingService: LoadingService,
    private apiService: ApiService,
    private apiUrlService: ApiUrlService,
    private dataService: DataService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Set grid context with router access
    this.gridContext = { componentParent: this, router: this.router };

    this.setupGrid();
    this.fetchBatchData();
    this.setupBreadcrumbs();
    window.addEventListener('resize', this.onWindowResize);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onWindowResize);
  }

  setupGrid(): void {
    this.columnDefs = BatchHeaderColDefs.map(col => {
      const lockPositionValue =
        col.lockPosition === 'left' || col.lockPosition === 'right'
          ? col.lockPosition
          : undefined;

      if (col.field === 'batch_id') {
        return {
          ...col,
          lockPosition: lockPositionValue,
          cellRenderer: BatchIdCellRendererComponent
        };
      }

      return {
        field: col.field,
        headerName: col.headerName,
        sortable: col.sortable ?? true,
        resizable: true,
        flex: col.flex,
        minWidth: col.minWidth,
        lockPosition: lockPositionValue,
        valueGetter: col.valueGetter
      };
    });

    // Configure grid options for responsiveness
    this.gridOptions = {
      suppressColumnVirtualisation: true, // Disable column virtualization for autoSizeAllColumns to work on all columns
      onFirstDataRendered: (params: any) => {
        // Auto-size all columns to content width once data is rendered
        if (this.gridApi) {
          this.gridApi.autoSizeAllColumns();
        }
      }
    };
  }

  fetchBatchData(): void {
    const endpoint = this.apiUrlService.getUrl('gatewayBaseUrl', 'batchList');
    this.apiService.getRequest(endpoint).subscribe({
      next: (response: any) => {
        this.rowData = response.items || [];
        // Cache rows in DataService so the batch details page can resolve extractHdrId without a second request.
        this.dataService.updateRowData(this.rowData);
        this.applyAllFilters();
      },
    });
  }

  setupBreadcrumbs(): void {
    this.breadcrumbs = [
      { label: 'Home', link: '/' },
      { label: 'Batch Headers', link: '/partner/Amazon' },
    ];
  }

  applyAllFilters(): void {
    const {
      startDate,
      endDate,
      importStep,
      transmitStep,
      transformationStep
    } = this.filters;

    const text = this.textFilter.toLowerCase();

    this.filteredRowData = this.rowData.filter(item => {
      const itemDate = new Date(item.start_time);

      const matchStart = !startDate || itemDate >= startDate;
      const matchEnd = !endDate || itemDate <= endDate;
      const matchImportStep = !importStep || item.import_step?.status?.toLowerCase().includes(importStep.toLowerCase());
      const matchTransmitStep = !transmitStep || item.transmit_step?.status?.toLowerCase().includes(transmitStep.toLowerCase());
      const matchTransformationStep = !transformationStep || item.transformation_step?.status?.toLowerCase().includes(transformationStep.toLowerCase());
      
      const matchText = !text || Object.values(item).some(val => {
        if (typeof val === 'string' || typeof val === 'number') {
          return String(val).toLowerCase().includes(text);
        }
        if (val && typeof val === 'object') {
          return (val as any).status?.toLowerCase().includes(text);
        }
        return false;
      });
      return matchStart && matchEnd && matchImportStep && matchTransmitStep && matchTransformationStep && matchText;
    });
  }

  applyTextFilter(event: Event): void {
    this.textFilter = (event.target as HTMLInputElement).value.trim();
    this.applyAllFilters();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.autoSizeAllColumns();
  }

  onFilterChange(): void {
    this.applyAllFilters();
  }

  onWindowResize = (): void => {
    if (this.gridApi) {
      // Auto-size columns to content on window resize
      this.gridApi.autoSizeAllColumns();
    }
  };

  onExport(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({
        fileName: 'studioema-batch-list-export.csv',
        allColumns: true
      });
    }
  }
}
